import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaArrowRight,
  FaClock,
  FaUser,
  FaTag,
  FaSearch,
  FaFire,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  banner: string;
  author: string;
  category: string;
  tags: string[];
  excerpt: string;
  date: string;
  metadata?: {
    readTime?: number;
  };
}

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const API_URL = import.meta.env.VITE_API_BACKEND_URL;
  const MAIN_SITE = import.meta.env.VITE_MAIN_SITE_URL || 'https://www.nostrahealth.com';

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/blogposts`);
      const data = await res.json();
      // Filter out posts without slugs to prevent navigation issues
      const validPosts = Array.isArray(data) ? data.filter((post: BlogPost) => post.slug) : [];
      setPosts(validPosts);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Array.from(new Set(posts.map((post) => post.category)))];

  const filteredPosts = posts.filter((post) => {
    const categoryMatch = selectedCategory === 'all' || post.category === selectedCategory;
    const searchMatch =
      !searchQuery ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags?.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return categoryMatch && searchMatch;
  });

  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + itemsPerPage);

  const getCategoryCount = (category: string) => {
    if (category === 'all') return posts.length;
    return posts.filter((p) => p.category === category).length;
  };

  return (
    <>
      <Helmet>
        <title>NostraHealth Blog - Healthcare AI Insights & Updates</title>
        <meta
          name="description"
          content="Explore the latest insights, tutorials, and news about AI-powered healthcare technology"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white">
        {/* Glassmorphic Header */}
        <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="text-2xl font-bold bg-gradient-to-r from-Color to-brightColor bg-clip-text text-transparent hover:scale-105 transition-transform"
              >
                NostraHealth <span className="text-sm font-normal text-gray-600">/ Blog</span>
              </Link>
              <nav className="hidden md:flex items-center gap-6">
                <a href={MAIN_SITE} className="text-gray-700 hover:text-Color transition-colors font-medium">
                  Home
                </a>
                <a href={`${MAIN_SITE}/about`} className="text-gray-700 hover:text-Color transition-colors font-medium">
                  About
                </a>
                <a
                  href={import.meta.env.VITE_DOCS_URL}
                  className="text-gray-700 hover:text-Color transition-colors font-medium"
                >
                  Documentation
                </a>
                <a
                  href={`${MAIN_SITE}/pricing`}
                  className="px-6 py-2.5 bg-gradient-to-r from-Color to-brightColor text-white rounded-full font-semibold hover:shadow-xl hover:scale-105 transition-all"
                >
                  Get Started
                </a>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section with Gradient */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-Color/20 via-brightColor/20 to-accentPurple/20"></div>
          <div className="relative max-w-7xl mx-auto px-6 py-16 text-center">
            <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-Color via-brightColor to-accentPurple bg-clip-text text-transparent">
              Healthcare AI Blog
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              Insights, tutorials, and news about AI-powered healthcare technology
            </p>

            {/* Search Bar - Glassmorphic */}
            <div className="max-w-2xl mx-auto">
              <div className="relative group">
                <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-Color text-lg group-focus-within:scale-110 transition-transform" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-14 pr-6 py-4 rounded-2xl backdrop-blur-xl bg-white/80 border-2 border-white/40 text-gray-900 text-lg focus:outline-none focus:border-Color/50 focus:shadow-2xl transition-all placeholder-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {/* Category Pills */}
              <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2 scrollbar-hide">
                {categories.slice(0, 6).map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setCurrentPage(1);
                    }}
                    className={`px-5 py-2.5 rounded-full font-semibold whitespace-nowrap transition-all ${
                      selectedCategory === category
                        ? 'bg-gradient-to-r from-Color to-brightColor text-white shadow-glass-hover scale-105'
                        : 'backdrop-blur-xl bg-white/60 text-gray-700 hover:bg-white/90 hover:shadow-glass border border-white/40 hover:scale-105'
                    }`}
                  >
                    {category === 'all' ? '🔥 All Posts' : category}
                  </button>
                ))}
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-Color border-t-transparent shadow-lg"></div>
                </div>
              )}

              {/* Blog Grid - Glassmorphic Cards */}
              {!loading && filteredPosts.length === 0 && (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">📝</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">No posts found</h3>
                  <p className="text-gray-600">Try adjusting your search or filter</p>
                </div>
              )}

              {!loading && paginatedPosts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {paginatedPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/${post.slug}`}
                      className="group backdrop-blur-xl bg-white/60 rounded-3xl overflow-hidden border border-white/40 hover:border-Color/50 shadow-glass hover:shadow-glass-hover transition-all duration-300 hover:scale-[1.03] hover:-translate-y-1"
                    >
                      {/* Image */}
                      {post.banner && (
                        <div className="relative overflow-hidden h-64">
                          <div className="absolute inset-0 bg-gradient-to-t from-darkPurple/70 via-darkPurple/30 to-transparent z-10"></div>
                          <img
                            src={post.banner}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-4 right-4 z-20">
                            <span className="px-4 py-1.5 backdrop-blur-xl bg-gradient-to-r from-Color to-brightColor text-white text-sm font-bold rounded-full shadow-glass">
                              {post.category}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-Color transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>

                        {/* Meta */}
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1.5">
                              <FaUser className="text-Color" />
                              {post.author}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <FaClock className="text-Color" />
                              {post.metadata?.readTime || 5} min
                            </span>
                          </div>
                          <FaArrowRight className="text-Color group-hover:translate-x-1 transition-transform" />
                        </div>

                        {/* Tags */}
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex items-center gap-2 mt-4 flex-wrap">
                            {post.tags.slice(0, 3).map((tag) => (
                              <span
                                key={`${post.id}-${tag}`}
                                className="px-3 py-1 backdrop-blur-xl bg-lightPurple/50 text-Color text-xs font-semibold rounded-full"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-xl backdrop-blur-xl bg-white/60 border border-white/40 text-gray-700 hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all flex items-center gap-2"
                  >
                    <FaChevronLeft /> Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-Color to-brightColor text-white shadow-lg'
                          : 'backdrop-blur-xl bg-white/60 border border-white/40 text-gray-700 hover:bg-white/90'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-xl backdrop-blur-xl bg-white/60 border border-white/40 text-gray-700 hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all flex items-center gap-2"
                  >
                    Next <FaChevronRight />
                  </button>
                </div>
              )}
            </div>

            {/* Right Sidebar - Categories & Popular */}
            <div className="hidden lg:block w-80 space-y-6">
              {/* Categories Card - Glassmorphic */}
              <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-6 border border-white/40 shadow-glass sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaFire className="text-Color" />
                  Categories
                </h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setCurrentPage(1);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all font-medium ${
                        selectedCategory === category
                          ? 'bg-gradient-to-r from-Color to-brightColor text-white shadow-glass-hover scale-105'
                          : 'bg-white/40 text-gray-700 hover:bg-white/70 hover:shadow-glass hover:scale-[1.02]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{category === 'all' ? 'All Posts' : category}</span>
                        <span className="text-sm opacity-75">({getCategoryCount(category)})</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Tags */}
              <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-6 border border-white/40 shadow-glass">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaTag className="text-Color" />
                  Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {Array.from(new Set(posts.flatMap((p) => p.tags || [])))
                    .slice(0, 12)
                    .map((tag) => (
                      <span
                        key={`popular-${tag}`}
                        className="px-3 py-1.5 backdrop-blur-xl bg-lightPurple/50 text-Color text-sm font-semibold rounded-full hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white hover:shadow-glass transition-all cursor-pointer hover:scale-110"
                      >
                        #{tag}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="backdrop-blur-xl bg-gradient-to-r from-Color/90 via-brightColor/90 to-accentPurple/90 rounded-3xl p-12 text-center shadow-glass-hover border border-white/20">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Transform Healthcare?</h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto text-lg">
              Sign up for NostraHealth and start building AI-powered healthcare solutions today
            </p>
            <a
              href={`${MAIN_SITE}/pricing`}
              className="inline-block bg-white text-Color px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl hover:scale-105"
            >
              Start Free Trial
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="backdrop-blur-xl bg-darkPurple/90 text-white py-12 mt-20 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-gray-300">© {new Date().getFullYear()} NostraHealth. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogList;
